import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Loader2, Layers } from "lucide-react";
import { TemplatePickerModal } from "./TemplatePickerModal";

const createStreamSchema = z.object({
  title: z.string().min(1, "Title is required"),
  blurb: z.string().min(1, "Blurb is required"),
  speakerId: z.string().min(1, "Speaker is required"),
  templateId: z.string().min(1, "Template is required"),
  date: z.string().min(1, "Date is required"),
  time: z.string().min(1, "Time is required"),
  platforms: z.array(z.string()).min(1, "Select at least one platform"),
});

type CreateStreamForm = z.infer<typeof createStreamSchema>;

interface CreateStreamModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface PlatformDescriptions {
  youtube: { title: string; description: string };
  facebook: { title: string; description: string };
  x: { title: string; description: string };
  twitch: { title: string; description: string };
}

export function CreateStreamModal({ isOpen, onClose }: CreateStreamModalProps) {
  const [descriptions, setDescriptions] = useState<PlatformDescriptions | null>(null);
  const [activeTab, setActiveTab] = useState("youtube");
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const form = useForm<CreateStreamForm>({
    resolver: zodResolver(createStreamSchema),
    defaultValues: {
      title: "",
      blurb: "",
      speakerId: "",
      templateId: "",
      date: new Date().toISOString().split('T')[0],
      time: "10:00",
      platforms: ["youtube"],
    },
  });

  const { data: speakers = [] } = useQuery<any[]>({
    queryKey: ["/api/speakers"],
  });

  const { data: templates = [] } = useQuery<any[]>({
    queryKey: ["/api/templates"],
  });

  const generateDescriptionsMutation = useMutation({
    mutationFn: async (data: Partial<CreateStreamForm>) => {
      const response = await apiRequest("POST", "/api/generate-descriptions", data);
      const result = await response.json();
      console.log('Generated descriptions:', result);
      return result;
    },
    onSuccess: (data) => {
      console.log('Setting descriptions:', data);
      setDescriptions(data);
      toast({
        title: "Descriptions generated!",
        description: "AI has created platform-optimized descriptions for your stream.",
      });
    },
    onError: (error) => {
      console.error('Description generation error:', error);
      toast({
        title: "Error",
        description: "Failed to generate descriptions. Please try again.",
        variant: "destructive",
      });
    },
  });

  const createStreamMutation = useMutation({
    mutationFn: async (data: CreateStreamForm & { descriptions?: PlatformDescriptions }) => {
      // Create the stream record
      const streamResponse = await apiRequest("POST", "/api/streams", {
        ...data,
        platforms: data.platforms.map(platform => ({ name: platform, enabled: true })),
        descriptions: data.descriptions,
        status: "draft",
      });
      
      let youtubeSuccess = false;
      
      // If YouTube is selected and we have descriptions, try to create the YouTube stream
      if (data.platforms.includes("youtube") && data.descriptions?.youtube) {
        try {
          await apiRequest("POST", "/api/youtube/create-stream", {
            title: data.descriptions.youtube.title,
            description: data.descriptions.youtube.description,
            scheduledStartTime: new Date(`${data.date}T${data.time}`).toISOString(),
          });
          youtubeSuccess = true;
        } catch (error) {
          console.error('YouTube stream creation failed:', error);
          // Continue with stream creation even if YouTube fails
        }
      }

      return { stream: await streamResponse.json(), youtubeSuccess, youtubeSelected: data.platforms.includes("youtube") };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/streams"] });
      queryClient.invalidateQueries({ queryKey: ["/api/youtube/streams"] });
      
      if (data.youtubeSelected && !data.youtubeSuccess) {
        toast({
          title: "Stream created with warnings",
          description: "Stream saved successfully, but YouTube creation failed. Please check your YouTube connection and try again.",
          variant: "default",
        });
      } else {
        toast({
          title: "Stream created!",
          description: "Your stream has been created on the selected platforms.",
        });
      }
      
      onClose();
      form.reset();
      setDescriptions(null);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to create stream. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleGenerateDescriptions = () => {
    const formData = form.getValues();
    generateDescriptionsMutation.mutate(formData);
  };

  const handleTemplateSelect = (template: any) => {
    if (!template) return;
    
    // Pre-fill form with template data
    const titleFormat = template.titleFormat.replace('{date}', new Date().toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric' 
    }));
    
    form.setValue('title', titleFormat);
    form.setValue('blurb', template.defaultBlurb || '');
    form.setValue('time', template.defaultTime || '10:00');
    form.setValue('platforms', template.defaultPlatforms || ['youtube']);
    
    toast({
      title: "Template applied!",
      description: `Using "${template.name}" template. You can still customize the stream details.`,
    });
  };

  const onSubmit = (data: CreateStreamForm) => {
    createStreamMutation.mutate({
      ...data,
      descriptions: descriptions || undefined,
    });
  };

  const platforms = [
    {
      id: "youtube",
      name: "YouTube Live",
      color: "bg-red-600",
      logo: (
        <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
        </svg>
      ),
    },
    {
      id: "facebook",
      name: "Facebook Live",
      color: "bg-blue-600",
      logo: (
        <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      ),
    },
    {
      id: "x",
      name: "X (formerly Twitter)",
      color: "bg-black",
      logo: (
        <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      ),
    },
    {
      id: "twitch",
      name: "Twitch",
      color: "bg-purple-600",
      logo: (
        <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
          <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/>
        </svg>
      ),
    },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Stream</DialogTitle>
          <p className="text-sm text-muted-foreground">Generate descriptions for all platforms at once</p>
        </DialogHeader>

        {/* Template Picker Button */}
        <div className="bg-muted/30 border border-border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Layers className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">Start with a Template</h4>
                <p className="text-xs text-muted-foreground">Quickly create streams from predefined templates</p>
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowTemplatePicker(true)}
              className="shrink-0"
            >
              <Layers className="w-4 h-4 mr-2" />
              Choose Template
            </Button>
          </div>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Basic Info Section */}
            <div className="space-y-4">
              <h4 className="font-semibold text-lg">Basic Information</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Stream Title</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., Sunday Morning Worship" data-testid="input-stream-title" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="speakerId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Speaker</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger data-testid="select-speaker">
                            <SelectValue placeholder="Select a speaker..." />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {speakers.map((speaker: any) => (
                            <SelectItem key={speaker.id} value={speaker.id}>
                              {speaker.firstName} {speaker.lastName}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="blurb"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Short Blurb/Message</FormLabel>
                    <FormControl>
                      <Textarea {...field} rows={3} placeholder="Brief description of the message (1-2 sentences)" data-testid="textarea-stream-blurb" />
                    </FormControl>
                    <p className="text-xs text-muted-foreground">This will be used by AI to generate platform-specific descriptions</p>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Date</FormLabel>
                      <FormControl>
                        <Input {...field} type="date" data-testid="input-stream-date" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="time"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Time</FormLabel>
                      <FormControl>
                        <Input {...field} type="time" data-testid="input-stream-time" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="templateId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Template</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger data-testid="select-template">
                          <SelectValue placeholder="Select a template..." />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {templates.map((template: any) => (
                          <SelectItem key={template.id} value={template.id}>
                            {template.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* AI Generation Section */}
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-6 h-6 text-primary-foreground" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold mb-1">AI-Powered Description Generator</h4>
                  <p className="text-sm text-muted-foreground mb-3">Click the button below to generate optimized descriptions for each platform using OpenAI</p>
                  <Button
                    type="button"
                    onClick={handleGenerateDescriptions}
                    disabled={generateDescriptionsMutation.isPending}
                    className="btn-primary px-6 py-2.5 rounded-lg font-medium flex items-center gap-2"
                    data-testid="button-generate-descriptions"
                  >
                    {generateDescriptionsMutation.isPending ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5" />
                        Generate Descriptions
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {/* Platform-Specific Descriptions */}
            {descriptions && (
              <div>
                <h4 className="font-semibold text-lg mb-4">Platform-Specific Descriptions</h4>
                
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="grid w-full grid-cols-4">
                    <TabsTrigger value="youtube">YouTube</TabsTrigger>
                    <TabsTrigger value="facebook">Facebook</TabsTrigger>
                    <TabsTrigger value="x">X (Twitter)</TabsTrigger>
                    <TabsTrigger value="twitch">Twitch</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="youtube" className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Title (max 100 characters)</label>
                      <Input
                        value={descriptions.youtube.title}
                        onChange={(e) => setDescriptions({
                          ...descriptions,
                          youtube: { ...descriptions.youtube, title: e.target.value }
                        })}
                        data-testid="input-youtube-title"
                      />
                      <p className="text-xs text-muted-foreground mt-1">{descriptions.youtube.title.length}/100 characters</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Description</label>
                      <Textarea
                        rows={8}
                        value={descriptions.youtube.description}
                        onChange={(e) => setDescriptions({
                          ...descriptions,
                          youtube: { ...descriptions.youtube, description: e.target.value }
                        })}
                        data-testid="textarea-youtube-description"
                      />
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="facebook" className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Title</label>
                      <Input
                        value={descriptions.facebook.title}
                        onChange={(e) => setDescriptions({
                          ...descriptions,
                          facebook: { ...descriptions.facebook, title: e.target.value }
                        })}
                        data-testid="input-facebook-title"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Description</label>
                      <Textarea
                        rows={6}
                        value={descriptions.facebook.description}
                        onChange={(e) => setDescriptions({
                          ...descriptions,
                          facebook: { ...descriptions.facebook, description: e.target.value }
                        })}
                        data-testid="textarea-facebook-description"
                      />
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="x" className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Title</label>
                      <Input
                        value={descriptions.x.title}
                        onChange={(e) => setDescriptions({
                          ...descriptions,
                          x: { ...descriptions.x, title: e.target.value }
                        })}
                        data-testid="input-x-title"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Description (280 characters max)</label>
                      <Textarea
                        rows={4}
                        value={descriptions.x.description}
                        onChange={(e) => setDescriptions({
                          ...descriptions,
                          x: { ...descriptions.x, description: e.target.value }
                        })}
                        data-testid="textarea-x-description"
                      />
                      <p className="text-xs text-muted-foreground mt-1">{descriptions.x.description.length}/280 characters</p>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="twitch" className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Title</label>
                      <Input
                        value={descriptions.twitch.title}
                        onChange={(e) => setDescriptions({
                          ...descriptions,
                          twitch: { ...descriptions.twitch, title: e.target.value }
                        })}
                        data-testid="input-twitch-title"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Description</label>
                      <Textarea
                        rows={6}
                        value={descriptions.twitch.description}
                        onChange={(e) => setDescriptions({
                          ...descriptions,
                          twitch: { ...descriptions.twitch, description: e.target.value }
                        })}
                        data-testid="textarea-twitch-description"
                      />
                    </div>
                  </TabsContent>
                </Tabs>
              </div>
            )}

            {/* Select Platforms */}
            <FormField
              control={form.control}
              name="platforms"
              render={() => (
                <FormItem>
                  <div className="mb-4">
                    <FormLabel className="text-lg font-semibold">Select Platforms to Stream</FormLabel>
                  </div>
                  <div className="space-y-2">
                    {platforms.map((platform) => (
                      <FormField
                        key={platform.id}
                        control={form.control}
                        name="platforms"
                        render={({ field }) => {
                          return (
                            <FormItem
                              key={platform.id}
                              className="flex flex-row items-start space-x-3 space-y-0 p-3 border border-border rounded-lg hover:bg-muted/30"
                            >
                              <FormControl>
                                <Checkbox
                                  checked={field.value?.includes(platform.id)}
                                  onCheckedChange={(checked) => {
                                    return checked
                                      ? field.onChange([...field.value, platform.id])
                                      : field.onChange(
                                          field.value?.filter(
                                            (value) => value !== platform.id
                                          )
                                        )
                                  }}
                                  data-testid={`checkbox-${platform.id}`}
                                />
                              </FormControl>
                              <div className="flex items-center gap-3 flex-1">
                                <div className={`w-8 h-8 ${platform.color} rounded flex items-center justify-center`}>
                                  {platform.logo}
                                </div>
                                <FormLabel className="font-medium cursor-pointer">
                                  {platform.name}
                                </FormLabel>
                              </div>
                            </FormItem>
                          )
                        }}
                      />
                    ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t border-border">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                data-testid="button-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createStreamMutation.isPending}
                className="btn-primary px-8 py-2.5 rounded-lg font-medium flex items-center gap-2"
                data-testid="button-create-stream-submit"
              >
                {createStreamMutation.isPending ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.348a1.125 1.125 0 010 1.971l-11.54 6.347a1.125 1.125 0 01-1.667-.985V5.653z" />
                    </svg>
                    Create Stream on All Platforms
                  </>
                )}
              </Button>
            </div>
          </form>
        </Form>

        {/* Template Picker Modal */}
        <TemplatePickerModal
          isOpen={showTemplatePicker}
          onClose={() => setShowTemplatePicker(false)}
          onSelect={handleTemplateSelect}
        />
      </DialogContent>
    </Dialog>
  );
}
